import React, { useState } from 'react';
import axios from 'axios';

/**
 * Textarea/input с кнопкой "Исправить ошибки" (орфография, пунктуация, опечатки).
 * Каждая найденная правка показывается отдельно с чекбоксом — можно снять галочку,
 * чтобы оставить конкретное слово как ввёл пользователь, не откатывая остальные правки.
 */
function GrammarCheckField({
  as = 'textarea',
  label,
  name,
  value,
  onChange,
  rows = 3,
  className = 'form-control',
  style,
  required,
  placeholder,
}) {
  const [matches, setMatches] = useState(null);
  const [checkedMap, setCheckedMap] = useState({});
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState('');

  const handleCheck = async () => {
    if (!value || !value.trim()) return;
    setChecking(true);
    setCheckError('');
    try {
      const res = await axios.post('/api/grammar/check', { text: value });
      const found = res.data.matches || [];
      setMatches(found);
      const initial = {};
      found.forEach((_, i) => {
        initial[i] = true;
      });
      setCheckedMap(initial);
    } catch (err) {
      setCheckError('Не удалось проверить текст. Попробуйте позже.');
      setMatches(null);
    } finally {
      setChecking(false);
    }
  };

  const applyChanges = () => {
    if (!matches) return;
    let text = value;
    [...matches]
      .map((m, i) => ({ ...m, i }))
      .sort((a, b) => b.offset - a.offset)
      .forEach((m) => {
        if (checkedMap[m.i]) {
          text = text.slice(0, m.offset) + m.replacement + text.slice(m.offset + m.length);
        }
      });
    onChange({ target: { name, value: text } });
    setMatches(null);
  };

  const dismiss = () => {
    setMatches(null);
    setCheckError('');
  };

  const Field = as;
  const fieldProps = {
    className,
    name,
    value,
    onChange,
    style,
    required,
    placeholder,
    ...(as === 'textarea' ? { rows } : { type: 'text' }),
  };

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        {label && <label className="form-label fw-bold mb-0">{label}</label>}
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={handleCheck}
          disabled={checking || !value || !value.trim()}
        >
          {checking ? 'Проверка...' : 'Исправить ошибки'}
        </button>
      </div>
      <Field {...fieldProps} />

      {checkError && <div className="text-danger small mt-1">{checkError}</div>}

      {matches && matches.length === 0 && (
        <div className="text-success small mt-1">Ошибок не найдено</div>
      )}

      {matches && matches.length > 0 && (
        <div className="border rounded p-2 mt-2 bg-light">
          <div className="small text-muted mb-2">
            Найдено исправлений: {matches.length}. Снимите галочку у пункта, чтобы оставить слово как есть.
          </div>
          {matches.map((m, i) => (
            <div key={i} className="form-check mb-1">
              <input
                type="checkbox"
                className="form-check-input"
                id={`${name}-grammar-match-${i}`}
                checked={!!checkedMap[i]}
                onChange={(e) => setCheckedMap((prev) => ({ ...prev, [i]: e.target.checked }))}
              />
              <label className="form-check-label small" htmlFor={`${name}-grammar-match-${i}`}>
                <s className="text-danger">{m.original}</s>
                {' → '}
                <strong className="text-success">{m.replacement || '—'}</strong>
                {m.message && <span className="text-muted"> ({m.message})</span>}
              </label>
            </div>
          ))}
          <div className="mt-2 d-flex gap-2">
            <button type="button" className="btn btn-sm btn-primary" onClick={applyChanges}>
              Применить выбранные
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={dismiss}>
              Отмена
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default GrammarCheckField;
